import { supabase } from './supabaseClient';

/**
 * Fetch mobile users with OneSignal player IDs (unique users)
 * @returns {Promise<{data: Array, count: number, error: string|null}>}
 */
export const fetchMobileUsers = async () => {
  try {
    // Get all player IDs with user information
    const { data: playerIdsData, error } = await supabase
      .from('user_player_ids')
      .select(`
        user_id,
        player_id,
        device_info,
        created_at,
        updated_at,
        users!inner(
          uuid,
          email,
          first_name,
          last_name,
          nickname,
          handle,
          profile_img,
          preferences,
          created_at,
          updated_at
        )
      `)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching mobile users:', error);
      return { data: [], count: 0, error: error.message };
    }

    // Transform the data to flatten the user information
    const transformedMobileUsers = playerIdsData?.map(item => ({
      uuid: item.users.uuid,
      email: item.users.email,
      first_name: item.users.first_name,
      last_name: item.users.last_name,
      nickname: item.users.nickname,
      handle: item.users.handle,
      profile_img: item.users.profile_img,
      preferences: item.users.preferences,
      created_at: item.users.created_at,
      updated_at: item.users.updated_at,
      player_id: item.player_id,
      device_info: item.device_info,
      player_created_at: item.created_at,
      player_updated_at: item.updated_at
    })) || [];

    // Get unique users (in case a user has multiple devices)
    const uniqueUsers = [];
    const seenUserIds = new Set();
    
    transformedMobileUsers.forEach(user => {
      if (!seenUserIds.has(user.uuid)) {
        seenUserIds.add(user.uuid);
        uniqueUsers.push(user);
      }
    });

    return { 
      data: uniqueUsers, 
      count: uniqueUsers.length, 
      error: null 
    };

  } catch (error) {
    console.error('Exception fetching mobile users:', error);
    return { data: [], count: 0, error: error.message };
  }
};

/**
 * Get just the count of mobile users (unique users)
 * @returns {Promise<{count: number, error: string|null}>}
 */
export const getMobileUsersCount = async () => {
  try {
    // Count unique users, not total player IDs
    const { data, error } = await supabase
      .from('user_player_ids')
      .select('user_id')
      .order('user_id');

    if (error) {
      console.error('Error getting mobile users count:', error);
      return { count: 0, error: error.message };
    }

    // Count unique user_ids
    const uniqueUserIds = new Set(data?.map(item => item.user_id) || []);
    const count = uniqueUserIds.size;

    return { count, error: null };

  } catch (error) {
    console.error('Exception getting mobile users count:', error);
    return { count: 0, error: error.message };
  }
};
